import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ApiDataSource } from '../../utils/api-data-source';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../api-request.service';
import { UtilsService } from '../utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { ToolsEditComponent } from 'src/app/tools/tools-edit/tools-edit.component';
import { ToolModel } from 'src/app/tools/tools.model';
import { ToolsFilterComponent } from '../../tools/tools-filter/tools-filter.component';
import { RBACService } from '../rbac.service';

@Component({
  selector: 'app-tools-selector',
  templateUrl: './tools-selector.component.html',
  styleUrls: ['./tools-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ToolsSelectorComponent implements OnInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'title',
    'description',
    'industry',
    'date_created'
    // 'date_created_UTC',
  ];
  dataSource: ToolsSelectorDataSource;
  returnAllSelected = false;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canCreate: boolean = false;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public dialogRef: MatDialogRef<ToolsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public rbac: RBACService
  ) {
    // set multiple if it is not present to false
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // construct the datasource
    this.dataSource = new ToolsSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // set a default selection of nothing if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // loop and select the values
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }

    this.returnAllSelected = this.data.returnAllSelected;
  }

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_TOOLS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );

    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      // Sort UTC by its corresponding date column.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  // clears the search bar and performs a search
  onClearSearch() {
    // clear the search field in the datasource
    this.dataSource.search = '';
    // search for records
    this.dataSource.getData(true);
  }

  onClearSelected() {
    this.dataSource.selection.clear();
  }

  /**
   * before the dialog close we want to store the new active work permit id
   */
  onSaveAndClose() {
    if (this.returnAllSelected) {
      return this.dialogRef.close({
        selected: this.dataSource.selection.selected,
        isAllSelected: this.dataSource.isAllSelected()
      });
    }

    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      if (this.dataSource.selection.selected.length) {
        response = this.dataSource.selection.selected[0];
      } else {
        response = 0;
      }
    }

    this.dialogRef.close(response);
  }

  onCreateNewTool() {
    // Layer 2: RBAC guard for creating tools.
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_TOOLS,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create tools.'
      );
      return;
    }

    // Create a new tool.
    this.utils
      .showComponentDialog(ToolsEditComponent, new ToolModel())
      .then((response) => {
        // Check if the response is a tool with an id
        if (response && typeof response.id != 'undefined' && response.id) {
          this.dataSource.selection.select(response.id);
        }

        // Refresh the tools selector list.
        this.dataSource.getData();
      });
  }

  /**
   * Open the list filters.
   */
  onOpenFilters() {
    this.utils
      .showComponentDialog(
        ToolsFilterComponent,
        {
          selected_industry_id: this.dataSource.industry,
          filter_archived_hidden: true
        },
        {
          width: '500px'
        }
      )
      .then((response) => {
        if (response) {
          this.dataSource.industry = response.industry_id;
          this.dataSource.getData(true);
        }
      });
  }

  onClose() {
    this.dialogRef.close();
  }

  protected readonly RBACService = RBACService;
}

export class ToolsSelectorDataSource extends ApiDataSource {
  order_by = 'title';
  order = 'asc';
  searchBy = 'title';
  industry: string = '';

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/tools', resetOffset, {
      searchBy: this.searchBy,
      include_archived: true,
      industryFilter: this.industry
    });
  }
}
